// The current-chat-id `sessionStorage` pair. It is not a Chat internal: the
// value is written by `useMutateCreateChat` after a chat is created and read as
// the panel's authorisation check on both chat routes and the embed, so it sits
// in the neutral zone where all three can reach it.
//
// The key string itself is `@heidi/evidence/contracts` — a persisted identity
// belongs in a `/contracts` key factory, and the read and write stay here
// because that zone is inert and these touch `globalThis.sessionStorage`.
import { CURRENT_CHAT_ID_STORAGE_KEY } from '@heidi/evidence/contracts';

/**
 * Persist the current chat ID in sessionStorage.
 * Used so the "current chat" can be recovered across in-app navigation within the same tab session.
 */
export const setCurrentChatId = (chatId: string | null | undefined) => {
  try {
    // Use globalThis so this works in tests and non-window runtimes (SSR-safe).
    const storage = (globalThis as typeof globalThis & { sessionStorage?: Storage })
      .sessionStorage;
    if (!storage) return;

    if (!chatId) {
      storage.removeItem(CURRENT_CHAT_ID_STORAGE_KEY);
      return;
    }

    storage.setItem(CURRENT_CHAT_ID_STORAGE_KEY, chatId);
  } catch {
    // ignore (storage may be blocked/unavailable)
  }
};

/**
 * Retrieve the current chat ID from sessionStorage.
 * Returns null if unavailable (SSR) or if storage is blocked/unavailable.
 */
export const getCurrentChatId = (): string | null => {
  try {
    const storage = (globalThis as typeof globalThis & { sessionStorage?: Storage })
      .sessionStorage;
    if (!storage) return null;
    return storage.getItem(CURRENT_CHAT_ID_STORAGE_KEY);
  } catch {
    return null;
  }
};
